import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import type {
  NavigationCustomizationItemDto,
  NavigationDashboardTabItemDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import { PageHeader } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  dashboardQueryKey,
  fetchDashboard,
} from '../../api/portalGateway/dashboard';
import {
  useDashboardEditModeStore,
  useIsDashboardDraftDirty,
} from '../../store/states/dashboardEditMode';
import { isNotNull, isNull } from '../../utils/nullish';
import { DashboardTopNavigation } from '../navigation/DashboardTopNavigation';
import { useDashboardUrlState } from '../navigation/useDashboardUrlState';
import { PageContentRenderer } from '../page/PageContentRenderer';
import { PageWithErrorBoundary } from '../page/PageWithErrorBoundary';
import { PageSidebarLayout } from '../page-sidebar/PageSidebarLayout';

import { DraftDashboardTile } from './utils/dashboard.types';
import { DashboardContentRenderer } from './DashboardContentRenderer';
import { DashboardEditActions } from './DashboardEditActions';
import { DashboardEmptyState } from './DashboardEmptyState';
import { DashboardLoadingState } from './DashboardLoadingState';
import { DashboardUnsavedChangesModal } from './DashboardUnsavedChangesModal';
import { usePageDashboardsQuery } from './usePageDashboardsQuery';

import pageHeaderActionsStyles from '../page/pageHeaderActions.module.scss';

type DashboardContainerProps = {
  sourcePageId: string;
  pageDashboardNavItems: NavigationDashboardTabItemDto[] | undefined;
  navigationKey: string | null;
  currentUserId: number | null | undefined;
  page: PageDto | undefined;
  navigationError?: { message: string } | null;
  pageError?: { message: string } | null;
  fallbackTitle?: string;
  pageHeaderNavigation?: ReactNode;
  slug?: string;
  currentDynamicNavigationItem?: NavigationCustomizationItemDto;
  classicPageActions?: ReactNode;
};

const EMPTY_DASHBOARD_VARIANTS = {
  noDashboards: {
    key: 'dashboard-empty-no-dashboards',
    testId: 'dashboard-empty-state-no-dashboards',
    title: 'app.dashboard.emptyState.noDashboards.title',
    description: 'app.dashboard.emptyState.noDashboards.description',
    action: 'app.dashboard.emptyState.noDashboards.action',
    modal: 'createDashboard',
    withAddIcon: true,
  },
  noInstances: {
    key: 'dashboard-empty-no-instances',
    testId: 'dashboard-empty-state-no-instances',
    title: 'app.dashboard.emptyState.noInstances.title',
    description: 'app.dashboard.emptyState.noInstances.description',
    action: 'app.dashboard.emptyState.noInstances.action',
    modal: 'allDashboards',
    withAddIcon: false,
  },
  emptyDashboard: {
    key: 'dashboard-empty-content',
    testId: 'dashboard-empty-state-empty-dashboard',
    title: 'app.dashboard.emptyState.emptyDashboard.title',
    description: 'app.dashboard.emptyState.emptyDashboard.descriptionOwner',
    descriptionViewer:
      'app.dashboard.emptyState.emptyDashboard.descriptionViewer',
    action: 'app.dashboard.emptyState.emptyDashboard.action',
    modal: 'addComponents',
    withAddIcon: true,
  },
} as const;

type EmptyDashboardVariant = keyof typeof EMPTY_DASHBOARD_VARIANTS;

function resolveEmptyDashboardVariant(
  pageDashboards: readonly unknown[] | null | undefined,
  hasPinnedDashboards: boolean,
  isDashboardView: boolean,
  isDashboardLoadingState: boolean,
  tileCount: number
): EmptyDashboardVariant | null {
  if (isNotNull(pageDashboards) && !hasPinnedDashboards) {
    if (pageDashboards.length === 0) {
      return 'noDashboards';
    }
    return 'noInstances';
  }
  if (isDashboardView && !isDashboardLoadingState && tileCount === 0) {
    return 'emptyDashboard';
  }
  return null;
}

export function DashboardContainer({
  sourcePageId,
  pageDashboardNavItems,
  navigationKey,
  currentUserId,
  page,
  navigationError,
  pageError,
  fallbackTitle,
  pageHeaderNavigation,
  slug,
  currentDynamicNavigationItem,
  classicPageActions,
}: DashboardContainerProps) {
  const { t } = useTranslation();
  const { activeDashboardId } = useDashboardUrlState(
    pageDashboardNavItems ?? []
  );

  const {
    data: hydratedDashboard,
    error: dashboardError,
    isLoading: isDashboardLoading,
  } = useQuery({
    queryKey: dashboardQueryKey(activeDashboardId ?? 'unknown'),
    queryFn: fetchDashboard,
    enabled: isNotNull(activeDashboardId),
  });

  const {
    data: pageDashboards,
    error: pageDashboardsError,
    isLoading: isPageDashboardsLoading,
  } = usePageDashboardsQuery(sourcePageId);

  const [
    exitDashboardEdit,
    enterDashboardEdit,
    clearPendingOpenInEdit,
    pendingOpenInEditDashboardId,
    requestOpenDashboardModal,
    dashboardDraftContents,
  ] = useDashboardEditModeStore(
    useShallow((state) => [
      state.exitDashboardEdit,
      state.enterDashboardEdit,
      state.clearPendingOpenInEdit,
      state.pendingOpenInEditDashboardId,
      state.requestOpenDashboardModal,
      state.editDashboardId === activeDashboardId ? state.draftContents : null,
    ])
  );
  const isDashboardDraftDirty = useIsDashboardDraftDirty(activeDashboardId);

  const hasPinnedDashboards = (pageDashboardNavItems?.length ?? 0) > 0;
  const isDashboardView = isNotNull(activeDashboardId) && hasPinnedDashboards;
  const isDashboardOwner =
    isNotNull(currentUserId) &&
    isNotNull(hydratedDashboard) &&
    currentUserId === hydratedDashboard.ownerUserId;

  const tiles: readonly DraftDashboardTile[] =
    dashboardDraftContents ?? hydratedDashboard?.contents ?? [];

  const isDashboardLoadingState =
    (!isDashboardView && isPageDashboardsLoading) ||
    (isDashboardView && isDashboardLoading && isNull(hydratedDashboard));

  const emptyDashboardVariant = resolveEmptyDashboardVariant(
    pageDashboards,
    hasPinnedDashboards,
    isDashboardView,
    isDashboardLoadingState,
    tiles.length
  );
  const emptyDashboard = isNotNull(emptyDashboardVariant)
    ? EMPTY_DASHBOARD_VARIANTS[emptyDashboardVariant]
    : null;
  const showEmptyDashboardAction =
    emptyDashboardVariant !== 'emptyDashboard' || isDashboardOwner;

  const routeLeaveBlocker = useBlocker(isDashboardDraftDirty);
  const [routeLeaveModalOpen, setRouteLeaveModalOpen] = useState(false);
  const isRouteLeaveBlocked = routeLeaveBlocker.state === 'blocked';

  useEffect(() => {
    if (isRouteLeaveBlocked) {
      setRouteLeaveModalOpen(true);
    }
  }, [isRouteLeaveBlocked]);

  const previousDashboardIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousId = previousDashboardIdRef.current;
    previousDashboardIdRef.current = activeDashboardId;
    if (isNull(previousId) || previousId === activeDashboardId) {
      return;
    }
    exitDashboardEdit();
  }, [activeDashboardId, exitDashboardEdit]);

  useEffect(() => {
    if (
      isNull(activeDashboardId) ||
      isNull(hydratedDashboard) ||
      isNull(pendingOpenInEditDashboardId) ||
      pendingOpenInEditDashboardId !== activeDashboardId
    ) {
      return;
    }
    if (!isDashboardOwner) {
      clearPendingOpenInEdit();
      return;
    }
    enterDashboardEdit(activeDashboardId, hydratedDashboard);
    clearPendingOpenInEdit();
  }, [
    activeDashboardId,
    clearPendingOpenInEdit,
    enterDashboardEdit,
    hydratedDashboard,
    isDashboardOwner,
    pendingOpenInEditDashboardId,
  ]);

  useEffect(() => {
    if (!isDashboardDraftDirty) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDashboardDraftDirty]);

  const handleDiscard = () => {
    setRouteLeaveModalOpen(false);
    exitDashboardEdit();
    if (isRouteLeaveBlocked) {
      routeLeaveBlocker.proceed();
    }
  };

  const handleStay = () => {
    setRouteLeaveModalOpen(false);
    if (isRouteLeaveBlocked) {
      routeLeaveBlocker.reset();
    }
  };

  const openEmptyDashboardAddComponent = () => {
    if (
      !isDashboardOwner ||
      isNull(activeDashboardId) ||
      isNull(hydratedDashboard)
    ) {
      return;
    }
    if (isNull(dashboardDraftContents)) {
      enterDashboardEdit(activeDashboardId, hydratedDashboard);
    }
    requestOpenDashboardModal('addComponents');
  };

  const enableDashboardActions =
    isDashboardOwner &&
    isNotNull(activeDashboardId) &&
    isNotNull(hydratedDashboard);

  const errorMessage =
    (
      navigationError ??
      pageError ??
      dashboardError ??
      (isDashboardView ? null : pageDashboardsError)
    )?.message ?? null;

  const dashboardDescription =
    isDashboardView && isNotNull(hydratedDashboard)
      ? hydratedDashboard.description?.trim()
      : null;

  const pageContent = () => {
    if (errorMessage) {
      return PageContentRenderer({
        pageNode: {
          type: 'error',
          message: errorMessage,
        },
        Render: PageContentRenderer,
        pageId: 'unknown',
        instanceId: 'unknown',
      });
    }
    if (isNotNull(emptyDashboard)) {
      const descriptionKey =
        'descriptionViewer' in emptyDashboard && !isDashboardOwner
          ? emptyDashboard.descriptionViewer
          : emptyDashboard.description;
      return (
        <DashboardEmptyState
          key={emptyDashboard.key}
          data-testid={emptyDashboard.testId}
          title={t(emptyDashboard.title)}
          description={t(descriptionKey)}
          actionLabel={
            showEmptyDashboardAction ? t(emptyDashboard.action) : undefined
          }
          onAction={
            showEmptyDashboardAction
              ? emptyDashboard.modal === 'addComponents'
                ? openEmptyDashboardAddComponent
                : () => requestOpenDashboardModal(emptyDashboard.modal)
              : undefined
          }
          withAddIcon={showEmptyDashboardAction && emptyDashboard.withAddIcon}
        />
      );
    }
    if (isDashboardLoadingState) {
      return <DashboardLoadingState />;
    }
    if (isDashboardView && isNotNull(activeDashboardId)) {
      return (
        <DashboardContentRenderer
          tiles={tiles}
          sourcePageId={sourcePageId}
          dashboardId={activeDashboardId}
        />
      );
    }
    return null;
  };

  return (
    <>
      <PageSidebarLayout
        navigationKey={navigationKey}
        topNavigation={
          <DashboardTopNavigation
            dashboardNavItems={pageDashboardNavItems}
            sourcePageId={sourcePageId}
          />
        }
      >
        <PageWithErrorBoundary
          pageHeaderNavigation={pageHeaderNavigation}
          pageHeader={
            fallbackTitle && (
              <>
                <PageHeader
                  title={
                    isDashboardView && isNotNull(hydratedDashboard)
                      ? hydratedDashboard.name
                      : page?.pageHeader?.title || fallbackTitle
                  }
                >
                  {isNotNull(page) && (
                    <div className={pageHeaderActionsStyles['actions']}>
                      {page.enablePageCustomization === true
                        ? enableDashboardActions && (
                            <DashboardEditActions
                              dashboardId={activeDashboardId}
                              hydratedDashboard={hydratedDashboard}
                              isOwner={isDashboardOwner}
                            />
                          )
                        : classicPageActions}
                    </div>
                  )}
                </PageHeader>
                {dashboardDescription && (
                  <p className={pageHeaderActionsStyles['dashboard-meta']}>
                    {dashboardDescription}
                  </p>
                )}
              </>
            )
          }
          pageFilter={null}
          pageContent={pageContent()}
          slug={currentDynamicNavigationItem?.href ?? slug}
          activeDynamicNavigationItem={currentDynamicNavigationItem}
          hasVisiblePageFilters={false}
        />
      </PageSidebarLayout>
      <DashboardUnsavedChangesModal
        opened={routeLeaveModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
    </>
  );
}
